import { Component, Element, Prop, h } from "@stencil/core";
import { Controller, Scene } from "scrollmagic";
import { isMobile, isTablet } from "../../utilities/screen";
import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";

@Component({
  tag: "scs-sticky-cta",
  styleUrl: "sticky-cta.scss",
  shadow: true,
})
export class ScsStickyCta {
  @Element() element!: HTMLElement;
  @Prop() public triggerSection: string = "";
  @Prop() public disableOnMobile = false;
  @Prop() public anchorId: string = "";
  @Prop() public desktopTitle: string = "";
  @Prop() public mobileTitle: string = "";
  @Prop() public description: string = "";
  @Prop() public imageSrc: string = "";
  @Prop() public buttonText: string = "";
  @Prop() public trackingEnabled = true;
  @Prop() public trackingAttributes = "{}";
  private controller?: Controller;

  private readonly trackByEvent: (event: Event) => void;
  private readonly adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;
  private readonly parsedTrackingAttributes: { [key: string]: string } = {};

  public constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
    this.trackByEvent = this.adobeAnalyticsTrackingService.trackByEvent.bind(
      this.adobeAnalyticsTrackingService,
    );
    if (this.trackingEnabled) {
      try {
        this.parsedTrackingAttributes = JSON.parse(this.trackingAttributes);
      } catch (error) {
        console.error("Error parsing tracking attributes:", error);
      }
    }
  }

  componentDidLoad() {
    this.controller = new Controller();
    const sectionElement = document.getElementById(this.triggerSection);

    if (sectionElement) {
      new Scene({
        triggerElement: sectionElement,
        triggerHook: 0.9,
      })
        .on("enter", () => {
          this.toggleVisibility(true);
        })
        .on("leave", () => {
          this.toggleVisibility(false);
        })
        .addTo(this.controller);
    }
  }

  disconnectedCallback() {
    this.controller?.destroy(true);
  }

  private toggleVisibility(show: boolean) {
    const shadowRootElement =
      this.element.shadowRoot?.querySelector(".sticky-cta");
    if (shadowRootElement) {
      shadowRootElement.classList.toggle("visible", show);
    }
  }

  render() {
    const shouldRender = !(isMobile() && this.disableOnMobile);

    return shouldRender ? (
      <div class="sticky-cta container">
        {!isTablet() && this.imageSrc && (
          <img class="cta-image" src={this.imageSrc} alt="CTA Image" />
        )}
        <div class="cta-content">
          <p class="h4 cta-title">
            {isMobile() ? this.mobileTitle : this.desktopTitle}
          </p>
          {!isMobile() && (
            <p class="text-small cta-description">{this.description}</p>
          )}
        </div>
        <sdx-button
          class="cta-button"
          icon-name="icon-arrow-up"
          label={this.buttonText}
          sr-hint="Open the link"
          {...this.parsedTrackingAttributes}
          onClick={(ev: Event) => {
            if (this.trackingEnabled) {
              this.trackByEvent(ev);
            }
            if (this.anchorId) {
              const targetElement = document.getElementById(this.anchorId);
              if (targetElement) {
                targetElement.scrollIntoView({ behavior: "smooth" });
              } else {
                console.warn(`Element with ID ${this.anchorId} not found.`);
              }
            }
          }}
        ></sdx-button>
      </div>
    ) : null;
  }
}
